import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { setClassNames } from '@ddc/classnames-js';
import { usePrefs, useSitemap } from 'wsm-common-data';

import { inventoryCountsSelector } from '../../server/selectors';
import { getModelLink } from '../../utils/getModelLink';
import { useShowMore } from '../../hooks/useShowMore';

import GridCard from '../cards/Grid';
import RowCard from '../cards/Row';
import ShowMoreButton from '../ShowMoreButton';

/**
 * Card-layout strategy map for the responsive display style. Keyed by the
 * `cardLayout` pref so new looks are added by extending this map, not by
 * growing an if/else chain (open for extension). A missing/unknown key falls
 * back to the stacked/current card.
 */
const cardComponents = {
	stacked: GridCard,
	row: RowCard
};

/**
 * Container-aware layout for the injected drawer/mega "Shop by Model" surfaces.
 *
 * Unlike the `col-*` GridContainer (which sizes columns off the viewport), this
 * uses a CSS grid `repeat(auto-fit, minmax(...))` that sizes columns off the
 * grid container's own width — yielding 1 column in the ~480px drawer panel,
 * 2 in the mega-menu column, and 3–4 as the container widens.
 */
const ResponsiveContainer = ({ vehicles }) => {
	const sitemap = useSitemap();
	const {
		cardLayout,
		modelLinkAlias,
		modelLinkParameters,
		noInventoryFallbackUrl
	} = usePrefs();
	const inventoryCounts = useSelector(inventoryCountsSelector);

	// Same initial-count cap + "Show More" reveal as the col-* grid, shared via
	// useShowMore. Unlike the grid (desktop-only button), the responsive layout
	// also serves the mobile drawer, so the button shows on any device once the
	// initial slice hides models.
	const { vehiclesToDisplay, hasMore, buttonLabel, showMore } =
		useShowMore(vehicles);

	const CardComponent = cardComponents[cardLayout] || GridCard;

	// The stacked look is the shared GridCard with its image moved on top; the
	// card exposes this as an `imageOnTop` prop (defaulting false) so the live
	// col-* grid stays title-first. The row card ignores the prop.
	const isStacked = cardLayout !== 'row';

	const containerClassNames = setClassNames([
		'model-responsive-container',
		`model-responsive-container--${
			cardLayout === 'row' ? 'row' : 'stacked'
		}`
	]);

	return (
		<>
			<div
				className={containerClassNames}
				data-testid="model-responsive-container"
			>
				{React.Children.toArray(
					vehiclesToDisplay.map((vehicle) => (
						<CardComponent
							modelObject={vehicle}
							imageOnTop={isStacked}
							modelLink={getModelLink(
								`${sitemap.get(modelLinkAlias)}`,
								modelLinkParameters,
								vehicle,
								noInventoryFallbackUrl,
								inventoryCounts
							)}
						/>
					))
				)}
			</div>

			{hasMore && (
				<ShowMoreButton label={buttonLabel} onClick={showMore} />
			)}
		</>
	);
};

ResponsiveContainer.propTypes = {
	vehicles: PropTypes.arrayOf(
		PropTypes.shape({
			year: PropTypes.number,
			make: PropTypes.string,
			model: PropTypes.string,
			modelInv: PropTypes.string,
			gvBodyStyle: PropTypes.string,
			image: PropTypes.string,
			inventoryCount: PropTypes.number,
			minStartingAtPrice: PropTypes.number
		})
	).isRequired
};

export default ResponsiveContainer;
